import { FilePlus, Loader2, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { SegmentedTabs } from '@/components/ui/segmented-tabs'
import type { LakeDocument } from '@/services/api/lake'
import { cn } from '@/utils/cn'
import { FileIcon, OutlineBadge, StatusPill } from './sections'
import type { DetailTab } from './types'

interface FileDetailHeaderProps {
  document: LakeDocument
  labelName: string
  labelColor: string | null
  activeTab: DetailTab
  onTabChange: (tab: DetailTab) => void
  onDelete?: () => Promise<void>
  /** Opens the parent's ExtractionScopeModal. Always rendered when provided;
      `destinationCaseId` only changes the modal's default case selection. */
  onImportToCase?: () => void
  /** Optional: when set, the modal is pre-selected to this case (and the
      parent navigates back to it after submit). Display-only here. */
  destinationCaseId?: string | null
}

export function FileDetailHeader({
  document,
  labelName,
  labelColor,
  activeTab,
  onTabChange,
  onDelete,
  destinationCaseId,
  onImportToCase,
}: FileDetailHeaderProps) {
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [deleting, setDeleting] = useState(false)

  const handleDelete = async () => {
    if (!onDelete) return
    setDeleting(true)
    try {
      await onDelete()
    } catch {
      setDeleting(false)
      setConfirmOpen(false)
    }
  }

  const status = document.indexing_status ?? document.status
  const showImport = Boolean(onImportToCase)
  // Phone: with the Import button or the delete prompt the actions need their own
  // line, otherwise they crush the tab strip (4 tabs need ~360 px on their own).
  const stackActions = showImport || confirmOpen

  return (
    <div
      className="max-md:!px-4 max-md:!pt-4"
      style={{
        position: 'sticky',
        top: 0,
        zIndex: 20,
        background: 'var(--glass-strong-bg)',
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
        borderBottom: '1px solid var(--border)',
        padding: '20px 24px 0 24px',
      }}
    >
      <div
        className="max-md:!pb-3"
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 14,
          paddingBottom: 16,
          // Right padding leaves room for the sheet's absolute close (X) button
          // that sits at top-3 right-3.
          paddingRight: 36,
        }}
      >
        <FileIcon kind={document.file_type} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <h2
            style={{
              margin: 0,
              fontSize: 15,
              fontWeight: 600,
              color: 'var(--foreground)',
              lineHeight: 1.35,
              wordBreak: 'break-word',
              marginBottom: 8,
            }}
          >
            {document.filename}
          </h2>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              flexWrap: 'wrap',
            }}
          >
            <StatusPill status={status} />
            {document.file_type && (
              <OutlineBadge tone="neutral" mono>
                {document.file_type.toUpperCase()}
              </OutlineBadge>
            )}
            <OutlineBadge
              tone="neutral"
              style={
                labelColor
                  ? {
                      borderColor: labelColor,
                      color: labelColor,
                      background: 'transparent',
                    }
                  : undefined
              }
            >
              {labelName}
            </OutlineBadge>
          </div>
        </div>
      </div>

      <div
        className={stackActions ? 'max-md:flex-wrap max-md:!gap-y-2' : undefined}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          paddingBottom: 12,
        }}
      >
        {/* Phone: tighter tab padding, so all four tabs fit the line without swiping. */}
        <div
          className={cn('max-md:[&_button]:!px-[7px]', stackActions && 'max-md:!basis-full')}
          style={{ flex: 1, minWidth: 0 }}
        >
          <SegmentedTabs<DetailTab>
            tabs={[
              { value: 'analysis', label: 'Overview' },
              { value: 'indexed-data', label: 'Indexed Data' },
              { value: 'imports', label: 'Imports' },
              { value: 'processing-stats', label: 'Processing Stats' },
            ]}
            active={activeTab}
            onChange={onTabChange}
            size="sm"
            flexWrap
          />
        </div>

        <div
          className={stackActions ? 'max-md:basis-full' : undefined}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            flexShrink: 0,
          }}
        >
          {showImport && (
            <button
              type="button"
              onClick={onImportToCase}
              // Phone: full-width primary action; it steps aside while the delete
              // prompt is open so a destructive Confirm never sits next to it.
              className={cn(
                'max-md:!h-9 max-md:flex-1 max-md:justify-center',
                confirmOpen && 'max-md:!hidden'
              )}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 5,
                height: 28,
                padding: '0 11px',
                borderRadius: '8px',
                background: 'var(--primary)',
                color: 'var(--primary-foreground)',
                border: '1px solid var(--primary)',
                fontFamily: 'inherit',
                fontSize: 11,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              <FilePlus size={12} />
              <span>Import to case</span>
            </button>
          )}

          {onDelete && !confirmOpen && (
            <button
              type="button"
              onClick={() => setConfirmOpen(true)}
              aria-label="Delete document"
              className="max-md:!size-9"
              style={{
                width: 28,
                height: 28,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '8px',
                background: 'transparent',
                border: '1px solid var(--border)',
                color: 'var(--muted-foreground)',
                cursor: 'pointer',
                transition: 'background 0.12s ease, color 0.12s ease, border-color 0.12s ease',
              }}
              onMouseEnter={e => {
                e.currentTarget.style.color = 'var(--destructive)'
                e.currentTarget.style.borderColor = 'var(--destructive)'
                e.currentTarget.style.background = 'var(--destructive-surface)'
              }}
              onMouseLeave={e => {
                e.currentTarget.style.color = 'var(--muted-foreground)'
                e.currentTarget.style.borderColor = 'var(--border)'
                e.currentTarget.style.background = 'transparent'
              }}
            >
              <Trash2 size={13} />
            </button>
          )}

          {confirmOpen && (
            <div
              className="max-md:flex-1 max-md:justify-end"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
              }}
            >
              <span style={{ fontSize: 11, color: 'var(--muted-foreground)' }}>Delete?</span>
              <button
                type="button"
                onClick={handleDelete}
                disabled={deleting}
                className="max-md:min-h-9"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 5,
                  padding: '5px 11px',
                  borderRadius: '8px',
                  background: 'var(--destructive)',
                  color: 'white',
                  border: '1px solid var(--destructive)',
                  fontFamily: 'inherit',
                  fontSize: 11,
                  fontWeight: 600,
                  cursor: deleting ? 'progress' : 'pointer',
                  opacity: deleting ? 0.7 : 1,
                }}
              >
                {deleting && <Loader2 size={11} className="animate-spin" />}
                {deleting ? 'Deleting…' : 'Confirm'}
              </button>
              <button
                type="button"
                onClick={() => setConfirmOpen(false)}
                disabled={deleting}
                className="max-md:min-h-9"
                style={{
                  padding: '5px 9px',
                  borderRadius: '8px',
                  background: 'transparent',
                  color: 'var(--foreground)',
                  border: '1px solid var(--border)',
                  fontFamily: 'inherit',
                  fontSize: 11,
                  fontWeight: 500,
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
